/**
 * ExportableCard — v2 section wrapper with per-card export menu.
 *
 * Wraps any analytics/report block so users can:
 *  - Export PNG (raster snapshot)
 *  - Export PDF (single-page, fitted)
 *  - Copy PNG to clipboard
 *  - Print just this card (uses an isolated print window)
 *  - Export CSV when tabular data is provided
 *
 * Heavy libs (html-to-image, jspdf) are loaded lazily on first use so
 * they don't bloat the initial AnalyticsDashboard bundle.
 */

import React, { useRef, useState, useCallback } from 'react';
import { MoreHorizontal, Download, Image as ImageIcon, Copy, Printer, FileText, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export interface ExportableCardProps {
  title: string;
  subtitle?: string;
  /** Slug used in the exported file name; falls back to a sanitized title */
  exportSlug?: string;
  /** Optional CSV rows: array of objects. First object's keys become headers. */
  csvRows?: Array<Record<string, unknown>>;
  /** Optional override of the CSV filename (without extension) */
  csvFilename?: string;
  /** Extra actions injected to the right of the menu (e.g. a date filter) */
  rightActions?: React.ReactNode;
  /** Force PDF orientation. Default: 'landscape' for wide cards (>800px). */
  pdfOrientation?: 'portrait' | 'landscape';
  children: React.ReactNode;
  className?: string;
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'report';

const stamp = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const downloadDataUrl = (dataUrl: string, filename: string) => {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  downloadDataUrl(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const toCsv = (rows: Array<Record<string, unknown>>): string => {
  if (!rows.length) return '';
  const keys = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = keys.join(',');
  const body = rows.map((r) => keys.map((k) => escape(r[k])).join(',')).join('\n');
  return `${header}\n${body}`;
};

export const ExportableCard: React.FC<ExportableCardProps> = ({
  title,
  subtitle,
  exportSlug,
  csvRows,
  csvFilename,
  rightActions,
  pdfOrientation,
  children,
  className,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<null | 'png' | 'pdf' | 'copy' | 'print'>(null);

  const baseName = `${exportSlug || slugify(title)}-${stamp()}`;

  const snapshot = useCallback(async (): Promise<string | null> => {
    if (!bodyRef.current) return null;
    const { toPng } = await import('html-to-image');
    // pixelRatio: 2 for sharp output. backgroundColor avoids transparent PNGs on dark mode.
    return toPng(bodyRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
      filter: (node) => {
        // Strip the action toolbar from snapshots (dropdown menus, etc.)
        if (node instanceof HTMLElement && node.dataset.exportIgnore === 'true') return false;
        return true;
      },
    });
  }, []);

  const handlePng = useCallback(async () => {
    try {
      setBusy('png');
      const dataUrl = await snapshot();
      if (dataUrl) downloadDataUrl(dataUrl, `${baseName}.png`);
    } catch (e) {
      console.error('PNG export failed', e);
      alert('PNG export failed. See console for details.');
    } finally {
      setBusy(null);
    }
  }, [snapshot, baseName]);

  const handlePdf = useCallback(async () => {
    try {
      setBusy('pdf');
      const dataUrl = await snapshot();
      if (!dataUrl || !bodyRef.current) return;
      const { jsPDF } = await import('jspdf');
      const rect = bodyRef.current.getBoundingClientRect();
      const orientation = pdfOrientation ?? (rect.width >= rect.height ? 'landscape' : 'portrait');
      const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Fit while preserving aspect ratio, leaving 32pt margins.
      const margin = 32;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2 - 40; // 40pt for header
      const scale = Math.min(maxW / rect.width, maxH / rect.height);
      const renderW = rect.width * scale;
      const renderH = rect.height * scale;
      // Header
      pdf.setFontSize(12);
      pdf.text(title, margin, margin + 6);
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      pdf.text(new Date().toLocaleString(), margin, margin + 22);
      pdf.setTextColor(0);
      pdf.addImage(dataUrl, 'PNG', margin + (maxW - renderW) / 2, margin + 40, renderW, renderH);
      pdf.save(`${baseName}.pdf`);
    } catch (e) {
      console.error('PDF export failed', e);
      alert('PDF export failed. See console for details.');
    } finally {
      setBusy(null);
    }
  }, [snapshot, baseName, pdfOrientation, title]);

  const handleCopy = useCallback(async () => {
    try {
      setBusy('copy');
      const dataUrl = await snapshot();
      if (!dataUrl) return;
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      } else {
        // Fallback: trigger a download since clipboard image write isn't supported.
        downloadBlob(blob, `${baseName}.png`);
      }
    } catch (e) {
      console.error('Copy failed', e);
      alert('Copy to clipboard failed. The PNG was downloaded instead if possible.');
    } finally {
      setBusy(null);
    }
  }, [snapshot, baseName]);

  const handleCsv = useCallback(() => {
    if (!csvRows?.length) return;
    const csv = toCsv(csvRows);
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${csvFilename || baseName}.csv`);
  }, [csvRows, baseName, csvFilename]);

  const handlePrint = useCallback(async () => {
    try {
      setBusy('print');
      const dataUrl = await snapshot();
      if (!dataUrl) return;
      const w = window.open('', '_blank', 'width=900,height=700');
      if (!w) {
        alert('Pop-up blocked — allow pop-ups to print this card.');
        return;
      }
      w.document.write(`<!doctype html><html><head><title>${title}</title>
        <style>
          html,body{margin:0;padding:24px;font-family:system-ui,sans-serif;}
          h1{font-size:14px;margin:0 0 4px;}
          .ts{font-size:11px;color:#666;margin-bottom:16px;}
          img{max-width:100%;height:auto;}
          @media print{ body{padding:0;} }
        </style></head><body>
        <h1>${title}</h1>
        <div class="ts">${new Date().toLocaleString()}</div>
        <img src="${dataUrl}" />
        <script>window.onload=()=>{setTimeout(()=>{window.print();},150);};</script>
      </body></html>`);
      w.document.close();
    } catch (e) {
      console.error('Print failed', e);
    } finally {
      setBusy(null);
    }
  }, [snapshot, title]);

  // When no title is provided, hide the header bar entirely and float the
  // export menu in the top-right of the body. Lets the card nest under a
  // page-level heading without duplicating the title.
  const showHeader = !!title;

  const exportMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-v2border bg-paper text-ink-500 hover:bg-paper-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-v2accent/40"
        aria-label="Export options"
        disabled={busy !== null}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={handlePng} className="gap-2">
          <ImageIcon className="h-4 w-4" /> Export PNG
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handlePdf} className="gap-2">
          <FileText className="h-4 w-4" /> Export PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopy} className="gap-2">
          <Copy className="h-4 w-4" /> Copy as PNG
        </DropdownMenuItem>
        {csvRows && csvRows.length > 0 && (
          <DropdownMenuItem onClick={handleCsv} className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handlePrint} className="gap-2">
          <Printer className="h-4 w-4" /> Print this card
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <section
      className={`v2-exportable-card relative rounded-md border border-v2border bg-surface ${className || ''}`}
      data-export-card={baseName}
    >
      {showHeader && (
        <header className="flex items-start gap-3 border-b border-v2border-muted px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-display text-base font-semibold tracking-[-0.01em] text-ink">
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 truncate text-xs text-ink-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 print:hidden" data-export-ignore="true">
            {rightActions}
            {exportMenu}
          </div>
        </header>
      )}
      {!showHeader && (
        <div
          className="absolute right-2 top-2 z-10 flex items-center gap-2 print:hidden"
          data-export-ignore="true"
        >
          {rightActions}
          {exportMenu}
        </div>
      )}
      <div ref={bodyRef} className="p-4">
        {children}
      </div>
    </section>
  );
};

export default ExportableCard;
